import React, { useEffect, useState } from 'react';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import './CompaniesPage.css';
import '../styles/rowActions.css';

const DEFAULT_HEX = '#cccccc';

export default function RibbonsPage() {
  const [ribbons, setRibbons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const [newColor, setNewColor] = useState('');
  const [newHex, setNewHex] = useState(DEFAULT_HEX);
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editColor, setEditColor] = useState('');
  const [editHex, setEditHex] = useState(DEFAULT_HEX);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadRibbons = () => {
    setLoading(true);
    return api.get('ribbons/')
      .then((r) => setRibbons(r.data))
      .catch(() => setStatus({ type: 'error', text: 'No se pudieron cargar las cintas.' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadRibbons(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newColor.trim()) return;
    setCreating(true);
    setStatus(null);
    try {
      const r = await api.post('ribbons/', { color: newColor.trim(), hex_color: newHex });
      setRibbons((rs) => [...rs, r.data].sort((a, b) => a.color.localeCompare(b.color)));
      setNewColor('');
      setNewHex(DEFAULT_HEX);
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudo crear la cinta.') });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditColor(r.color);
    setEditHex(r.hex_color || DEFAULT_HEX);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditColor('');
    setEditHex(DEFAULT_HEX);
  };

  const handleSaveEdit = async (id) => {
    if (!editColor.trim()) return;
    setSavingId(id);
    setStatus(null);
    try {
      const r = await api.patch(`ribbons/${id}/`, { color: editColor.trim(), hex_color: editHex });
      setRibbons((rs) => rs.map((x) => (x.id === id ? r.data : x)));
      cancelEdit();
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudieron guardar los cambios.') });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta cinta?')) return;
    setDeletingId(id);
    setStatus(null);
    try {
      await api.delete(`ribbons/${id}/`);
      setRibbons((rs) => rs.filter((x) => x.id !== id));
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo eliminar la cinta.' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="companies-root">
      <h1 className="companies-title">🎀 Cintas</h1>

      <form className="companies-add-form" onSubmit={handleCreate}>
        <input
          className="companies-add-input"
          placeholder="Nuevo color de cinta"
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
        />
        <input
          type="color"
          className="ribbons-color-input"
          value={newHex}
          onChange={(e) => setNewHex(e.target.value)}
          title="Color de muestra"
        />
        <button className="companies-add-btn" type="submit" disabled={creating || !newColor.trim()}>
          {creating ? 'Agregando…' : '+ Agregar cinta'}
        </button>
      </form>

      {status && <div className={`companies-status companies-status-${status.type}`}>{status.text}</div>}

      <div className="companies-card">
        {loading ? (
          <div className="companies-empty">Cargando…</div>
        ) : ribbons.length === 0 ? (
          <div className="companies-empty">Aún no hay cintas.</div>
        ) : (
          <table className="companies-table">
            <thead>
              <tr>
                <th>Muestra</th>
                <th>Color</th>
                <th>Creado por</th>
                <th>Creado</th>
                <th>Editado por</th>
                <th>Modificado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ribbons.map((r) => (
                <tr key={r.id}>
                  <td>
                    {editingId === r.id ? (
                      <input
                        type="color"
                        className="ribbons-color-input"
                        value={editHex}
                        onChange={(e) => setEditHex(e.target.value)}
                      />
                    ) : (
                      <span className="ribbons-swatch" style={{ backgroundColor: r.hex_color || DEFAULT_HEX }} />
                    )}
                  </td>
                  <td>
                    {editingId === r.id ? (
                      <input
                        className="companies-edit-input"
                        value={editColor}
                        onChange={(e) => setEditColor(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      r.color
                    )}
                  </td>
                  <td>{r.created_by_username || '—'}</td>
                  <td>{new Date(r.created).toLocaleDateString()}</td>
                  <td>{r.last_edited_by_username || '—'}</td>
                  <td>{new Date(r.modified).toLocaleDateString()}</td>
                  <td>
                    <div className="row-actions">
                      {editingId === r.id ? (
                        <>
                          <button
                            className="row-icon-btn"
                            onClick={() => handleSaveEdit(r.id)}
                            disabled={savingId === r.id}
                            title="Guardar"
                            aria-label="Guardar"
                          >
                            {savingId === r.id ? '…' : '💾'}
                          </button>
                          <button className="row-icon-btn" onClick={cancelEdit} title="Cancelar" aria-label="Cancelar">✖️</button>
                        </>
                      ) : (
                        <>
                          <button className="row-icon-btn" onClick={() => startEdit(r)} title="Editar" aria-label="Editar">✏️</button>
                          <button
                            className="row-icon-btn"
                            onClick={() => handleDelete(r.id)}
                            disabled={deletingId === r.id}
                            title="Eliminar"
                            aria-label="Eliminar"
                          >
                            {deletingId === r.id ? '…' : '🗑️'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
